import {toBlob, toPng} from 'html-to-image';

/** Fetch the HF logo SVG once and cache as a data URL for use in image exports. */
let hfLogoDataUrl: string | null = null;
async function getHfLogoDataUrl(): Promise<string | null> {
  if (hfLogoDataUrl) return hfLogoDataUrl;
  try {
    const response = await fetch('/assets/hf-logo.svg');
    if (!response.ok) return null;
    const svgText = await response.text();
    hfLogoDataUrl = `data:image/svg+xml,${encodeURIComponent(svgText)}`;
    return hfLogoDataUrl;
  } catch {
    return null;
  }
}

export type ChartExport = {
  vizEl: HTMLElement;
  title: string;
  subtitle?: string;
  /** Caption for the footer, e.g. the issue time of the displayed forecast. */
  annotation?: string;
};

/** Copy the chart, dropping series the user has unchecked in the legend. */
export function cloneVisibleContents(vizEl: HTMLElement): HTMLElement {
  // An expanded legend lists the same id twice, so collect ids before removing.
  const hiddenIds = new Set<string>();
  vizEl.querySelectorAll<HTMLElement>('[data-legend-id]').forEach((el) => {
    const checkbox = el.querySelector<HTMLInputElement>('input[type="checkbox"]');
    const unchecked = checkbox ? !checkbox.checked : el.getAttribute('aria-pressed') === 'false';
    if (unchecked) hiddenIds.add(el.dataset.legendId!);
  });

  const copy = vizEl.cloneNode(true) as HTMLElement;
  hiddenIds.forEach((id) => {
    copy.querySelectorAll(`[data-legend-id="${id}"]`).forEach((el) => el.remove());
  });
  copy.querySelectorAll('[data-export-omit]').forEach((el) => el.remove());
  // A pressed toggle stays as the label of what it shows; other controls go.
  copy
    .querySelectorAll('input[type="checkbox"], button:not([aria-pressed="true"])')
    .forEach((el) => el.remove());
  return copy;
}

/** Render the chart off-screen with a title and HF logo footer, then rasterize it. */
async function withExportContainer<T>(
  {vizEl, title, subtitle, annotation}: ChartExport,
  rasterize: (contents: HTMLElement) => Promise<T>
): Promise<T> {
  const container = document.createElement('div');
  container.classList.add('absolute');
  container.style.left = '-9999px';
  document.body.appendChild(container);

  try {
    const contents = document.createElement('div');
    contents.classList.add(
      'relative',
      'p-6',
      'flex',
      'flex-col',
      'items-center',
      'gap-6',
      'bg-white'
    );
    contents.style.width = `${vizEl.offsetWidth + 48}px`;
    container.appendChild(contents);

    const heading = document.createElement('h1');
    heading.classList.add('text-lg');
    heading.innerText = title;
    contents.appendChild(heading);

    if (subtitle) {
      const subtitleEl = document.createElement('div');
      subtitleEl.classList.add('text-sm', 'text-gray-600', '-mt-4');
      subtitleEl.innerText = subtitle;
      contents.appendChild(subtitleEl);
    }

    contents.appendChild(cloneVisibleContents(vizEl));

    const footer = document.createElement('div');
    footer.classList.add('flex', 'items-center', 'justify-between', 'w-full', 'px-2');

    const annotationEl = document.createElement('div');
    annotationEl.classList.add('text-xs', 'text-gray-500');
    annotationEl.innerText = annotation ?? '';
    footer.appendChild(annotationEl);

    const logoDataUrl = await getHfLogoDataUrl();
    if (logoDataUrl) {
      const logo = document.createElement('img');
      logo.src = logoDataUrl;
      logo.alt = 'HydroForecast';
      logo.style.height = '20px';
      logo.style.width = 'auto';
      footer.appendChild(logo);
    }

    contents.appendChild(footer);

    return await rasterize(contents);
  } finally {
    document.body.removeChild(container);
  }
}

export const chartToPng = (chart: ChartExport) => withExportContainer(chart, toPng);

export const chartToBlob = (chart: ChartExport) => withExportContainer(chart, toBlob);
